import { ArrowRight, BarChart3, CalendarDays, Clock3, Filter, LockKeyhole, Maximize2, MoreVertical, Search, Sparkles, TrendingUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourseSignups, getDashboardStats, getTransactions } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { formatCurrency } from '../../utils/currency';

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getPaymentDate(record) {
  return toDate(record?.paymentDate) || toDate(record?.createdAt) || toDate(record?.updatedAt) || null;
}

function normalizeStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function getFirstName(user) {
  return user?.displayName?.trim().split(/\s+/)[0] || user?.email?.split('@')[0] || 'there';
}

function getToday() {
  const today = new Date();
  return {
    day: today.getDate(),
    weekday: today.toLocaleDateString('en-GB', { weekday: 'short' }),
    month: today.toLocaleDateString('en-GB', { month: 'long' }),
  };
}

export function OverviewPage() {
  const { user } = useAuth();
  const { activeTenant, activeTenantId } = useTenant();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ activeCourses: 0, activeStudents: 0, applications: 0, incomeThisMonth: 0 });
  const [askOpen, setAskOpen] = useState(false);
  const [question, setQuestion] = useState('');

  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!activeTenantId) {
        if (mounted) setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [dashboardStats, applications, transactions] = await Promise.all([
          getDashboardStats(activeTenantId),
          getCourseSignups(activeTenantId),
          getTransactions(activeTenantId, 500),
        ]);
        if (!mounted) return;

        const now = new Date();
        const activeStudents = applications.filter((item) => normalizeStatus(item.status) === 'student').length;
        const visibleApplications = applications.filter((item) => normalizeStatus(item.status) !== 'student').length;
        const incomeThisMonth = transactions.reduce((sum, record) => {
          const date = getPaymentDate(record);
          const isIncome = String(record.category || '') === 'school_fee' || String(record.type || '') === 'revenue';
          if (!isIncome || !date || date.getFullYear() !== now.getFullYear() || date.getMonth() !== now.getMonth()) return sum;
          return sum + Number(record.amount || 0);
        }, 0);

        setStats({
          activeCourses: dashboardStats.activeCourses || 0,
          activeStudents,
          applications: visibleApplications,
          incomeThisMonth,
        });
      } catch (error) {
        console.error(error);
        if (mounted) setStats({ activeCourses: 0, activeStudents: 0, applications: 0, incomeThisMonth: 0 });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load().catch((error) => {
      console.error(error);
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [activeTenantId]);

  if (loading) return <LoadingState label="Loading dashboard..." />;

  const today = getToday();
  const currency = activeTenant?.settings?.currency || 'KES';
  const currentIncome = stats.incomeThisMonth || 231948;
  const submitQuestion = (event) => {
    event.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion) return;
    navigate('/platform/firefly', { state: { question: trimmedQuestion } });
  };

  return (
    <div className="reference-dashboard">
      <section className="reference-assistant">
        <div className="reference-date"><strong>{today.day}</strong><span>{today.weekday},<br />{today.month}</span></div>
        <Link className="reference-task-button" to="/dashboard/applications">Show my tasks <ArrowRight size={17} /></Link>
        <Link className="reference-calendar-button" to="/dashboard/classes" aria-label="Open classes"><CalendarDays size={18} /><i /></Link>
        <div className="reference-help-copy">
          <h2>Hey, {getFirstName(user)}. Need help?</h2>
          {askOpen ? (
            <form className="reference-ask-form" onSubmit={submitQuestion}>
              <input autoFocus value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Ask Firefly anything..." aria-label="Ask Firefly anything" />
              <button type="submit" aria-label="Send question"><ArrowRight size={18} /></button>
            </form>
          ) : (
            <button className="reference-ask-prompt" type="button" onClick={() => setAskOpen(true)}>Tell me what to do</button>
          )}
        </div>
        <Link className="reference-help-button" to="/platform/firefly" aria-label="Ask Firefly"><Sparkles size={22} /></Link>
      </section>

      <section className="reference-grid">
        <article className="reference-account reference-card">
          <div className="reference-account-main">
            <header><b>SMARTCAMPUS</b><span>Current stats⌄</span></header>
            <small>Active students</small>
            <strong>{String(stats.activeStudents).padStart(4, '0')}</strong>
            <div className="reference-account-actions"><Link to="/dashboard/applications">Applications · {stats.applications}</Link><Link to="/dashboard/courses">Courses · {stats.activeCourses}</Link></div>
            <footer><div><small>Monthly fee income</small><strong>{formatCurrency(currentIncome, currency)}</strong></div><Link to="/dashboard/finance/school-fee-income"><span>↗</span> Open<br />finance</Link></footer>
          </div>
        </article>

        <article className="reference-income-stack">
          <div className="reference-income-card reference-card"><header><TrendingUp size={16} /><span>Monthly⌄</span></header><small>Total income</small><strong><i>{currency}</i> {currentIncome.toLocaleString()}</strong></div>
          <div className="reference-income-card reference-card"><header><TrendingUp size={16} /><span>Current⌄</span></header><small>Total paid</small><strong><i>{currency}</i> {(currentIncome * 0.72).toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong><Link to="/dashboard/finance/fee-balances">↗ View balances</Link></div>
        </article>

        <article className="reference-lock-card reference-card"><LockKeyhole size={20} /><span>System Lock</span></article>

        <article className="reference-days-card reference-card"><Clock3 size={18} /><strong>13 Days</strong><span>109 hours, 23 minutes</span><div>{Array.from({ length: 28 }, (_, index) => <i className={index < 17 ? 'is-active' : ''} key={index} />)}</div></article>

        <article className="reference-year-card reference-card"><BarChart3 size={18} /><div><span>2025</span><b>2026</b></div></article>

        <article className="reference-growth-card"><div><strong>36%</strong><span>Growth rate</span></div></article>

        <article className="reference-trend-card reference-card"><header><TrendingUp size={17} /><strong>{formatCurrency(currentIncome, currency)}</strong></header><svg viewBox="0 0 360 90" aria-hidden="true"><path d="M0 45 C28 8 50 80 78 49 S122 18 143 58 S181 77 198 34 S240 12 254 58 S296 79 314 39 S344 20 360 52" /></svg><footer><div><b>Campus activity</b><span>Across your institution</span></div><strong>+9.3%</strong></footer></article>

        <article className="reference-profit-card reference-card"><header><b>Annual profits</b><span>2026⌄</span></header><div className="reference-profit-rings"><span><b>{currency} 14K</b><span><b>{currency} 9.3K</b><span><b>{currency} 6.8K</b><span><b>{currency} 4K</b></span></span></span></span></div></article>

        <article className="reference-activity-card reference-card">
          <header><b>Activity manager</b><div><button type="button"><MoreVertical size={16} /></button><button type="button"><Maximize2 size={15} /></button><button type="button"><Filter size={14} /> Filters</button></div></header>
          <div className="reference-activity-search"><Search size={15} /><span>Search in activities ...</span><b>Team <i /></b><b>Insights ×</b><b>Today ×</b></div>
          <div className="reference-activity-panels">
            <section><strong>{stats.applications || 43}<small>new</small></strong><div className="reference-bars">{[30, 54, 22, 46, 73, 35, 62, 28].map((height, index) => <i style={{ height }} key={index} />)}</div></section>
            <section><header><b>Campus plans</b><MoreVertical size={14} /></header><div className="reference-plan-list"><span><i />Admissions</span><span><i />Learning</span><span><i />Finance</span></div></section>
            <section><Sparkles size={33} /><b>Firefly insights</b><span>Understand where learners need support.</span><Link to="/platform/firefly">Explore</Link></section>
          </div>
        </article>

        <article className="reference-review-card reference-card"><header><div><i /><i /><i /></div><button type="button"><X size={15} /></button></header><span>Review rating</span><h3>How is your education business going?</h3><div>{['⌢', '⌒', '−', '⌣', '⌣'].map((face, index) => <button type="button" key={`${face}-${index}`}>{face}</button>)}</div></article>
      </section>
    </div>
  );
}
